import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fornitoriApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, MoreVertical, Eye, Trash2, Radio, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const FornitoriPage = () => {
  const navigate = useNavigate();
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedFornitore, setSelectedFornitore] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newFornitore, setNewFornitore] = useState({ denominazione_sociale: '', comune: '', provincia: '' });

  const fetchFornitori = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fornitoriApi.getAll(searchQuery);
      setFornitori(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei fornitori');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchFornitori(), 300);
    return () => clearTimeout(debounce);
  }, [fetchFornitori]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newFornitore.denominazione_sociale.trim()) { toast.error('La denominazione sociale è obbligatoria'); return; }
    setCreateLoading(true);
    try {
      const response = await fornitoriApi.create(newFornitore);
      toast.success(`Fornitore creato con codice ${response.data.codice}`);
      setShowCreateDialog(false);
      setNewFornitore({ denominazione_sociale: '', comune: '', provincia: '' });
      navigate(`/fornitori/${response.data.id}`);
    } catch (error) {
      toast.error('Errore nella creazione');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedFornitore) return;
    setDeleteLoading(true);
    try {
      await fornitoriApi.delete(selectedFornitore.id);
      toast.success('Fornitore eliminato');
      setShowDeleteDialog(false);
      setSelectedFornitore(null);
      fetchFornitori();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getEmittentiCount = (fornitore) => {
    let count = 0;
    if (fornitore.emittente1?.nome) count++;
    if (fornitore.emittente2?.nome) count++;
    if (fornitore.emittente3?.nome) count++;
    return count;
  };

  return (
    <div className="p-6 md:p-10" data-testid="fornitori-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900 tracking-tight">Fornitori</h1>
          <p className="text-slate-500 mt-1">Gestisci le radio e l'inventario spot pubblicitari</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-fornitore-btn" className="bg-primary hover:bg-primary/90">
          <Plus size={18} className="mr-2" />Nuovo Fornitore
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Cerca per codice, denominazione, comune o emittente..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-fornitori-input" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="fornitori-table-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Radio size={20} className="text-primary" />Lista Fornitori ({fornitori.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : fornitori.length === 0 ? (
            <div className="text-center py-12 empty-state rounded-lg">
              <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Nessun fornitore trovato</h3>
              <p className="text-slate-500 mb-4">{searchQuery ? 'Prova a modificare i criteri di ricerca' : 'Inizia aggiungendo il primo fornitore'}</p>
              {!searchQuery && <Button onClick={() => setShowCreateDialog(true)}><Plus size={18} className="mr-2" />Aggiungi Fornitore</Button>}
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold w-[100px]">Codice</TableHead>
                    <TableHead className="font-semibold">Denominazione Sociale</TableHead>
                    <TableHead className="font-semibold">Comune</TableHead>
                    <TableHead className="font-semibold">Provincia</TableHead>
                    <TableHead className="font-semibold">Emittenti</TableHead>
                    <TableHead className="font-semibold">Referente</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fornitori.map((fornitore) => (
                    <TableRow key={fornitore.id} className="table-row-animate cursor-pointer" onClick={() => navigate(`/fornitori/${fornitore.id}`)} data-testid={`fornitore-row-${fornitore.id}`}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{fornitore.codice}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{fornitore.denominazione_sociale}</TableCell>
                      <TableCell>{fornitore.comune || '-'}</TableCell>
                      <TableCell>{fornitore.provincia || '-'}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {getEmittentiCount(fornitore)} emittenti
                        </span>
                      </TableCell>
                      <TableCell>{fornitore.referente1_nome || '-'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical size={16} /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/fornitori/${fornitore.id}`)}><Eye className="mr-2 h-4 w-4" />Visualizza</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => { setSelectedFornitore(fornitore); setShowDeleteDialog(true); }}><Trash2 className="mr-2 h-4 w-4" />Elimina</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-fornitore-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Nuovo Fornitore</DialogTitle>
            <DialogDescription>Inserisci i dati del nuovo fornitore (radio). Il codice verrà assegnato automaticamente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="denominazione">Denominazione Sociale *</Label>
                <Input id="denominazione" value={newFornitore.denominazione_sociale} onChange={(e) => setNewFornitore({ ...newFornitore, denominazione_sociale: e.target.value })} placeholder="Es: Radio Italia Network S.r.l." data-testid="input-denominazione" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="comune">Comune</Label>
                  <Input id="comune" value={newFornitore.comune} onChange={(e) => setNewFornitore({ ...newFornitore, comune: e.target.value })} placeholder="Es: Milano" data-testid="input-comune" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="provincia">Provincia</Label>
                  <Input id="provincia" value={newFornitore.provincia} onChange={(e) => setNewFornitore({ ...newFornitore, provincia: e.target.value })} placeholder="Es: MI" data-testid="input-provincia" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={createLoading} data-testid="submit-create-fornitore">{createLoading ? 'Creazione...' : 'Crea Fornitore'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-red-600">Elimina Fornitore</DialogTitle>
            <DialogDescription>Sei sicuro di voler eliminare <strong>{selectedFornitore?.codice} - {selectedFornitore?.denominazione_sociale}</strong>?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteLoading}>{deleteLoading ? 'Eliminazione...' : 'Elimina'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FornitoriPage;
